import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Button,
  Chip,
  Paper,
  Typography,
  IconButton,
  Collapse,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import {
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import AdminApiModule from "../../../components/AdminApi/Api/api";

const CloseTicketFilter = ({
  onFilterSubmit,
  onClearFilters,
  loading = false,
  initialFilters = {},
}) => {
  const [expanded, setExpanded] = useState(false);
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [filters, setFilters] = useState(() => {
    const savedFilters = localStorage.getItem("closeTicketFilters");
    return savedFilters
      ? JSON.parse(savedFilters)
      : {
          start_date: moment().subtract(1, "month").format("YYYY-MM-DD"),
          end_date: moment().format("YYYY-MM-DD"),
          ...initialFilters,
        };
  });

  // Filter options specific to closed tickets
  const ticketCreatedByOptions = [
    { value: "SUPER_ADMIN", label: "Super Admin" },
    { value: "CENTER", label: "Center" },
    { value: "CLIENT", label: "Client" },
    { value: "PUBLIC", label: "Public" },
    { value: "PUBLIC_APP", label: "Public App" },
    { value: "DEALER", label: "Dealer" },
    { value: "PUBLIC_WITH_CENTER_QR", label: "Public with Center QR" },
    { value: "PUBLIC_WITH_CLIENT_QR", label: "Public with Client QR" },
    { value: "PUBLIC_WITH_DELAR_QR", label: "Public with Dealer QR" },
    { value: "PUBLIC_WITH_SOCIETY_QR", label: "Public with Society QR" },
    { value: "IMPORT_VIA_EXCEL", label: "Import via Excel" },
  ];

  const booleanOptions = [
    { value: "", label: "All" },
    { value: "true", label: "Yes" },
    { value: "false", label: "No" },
  ];

  // Load filters from localStorage with unique key for closed tickets
  useEffect(() => {
    const savedFilters = localStorage.getItem("closeTicketFilters");
    if (savedFilters) {
      const parsedFilters = JSON.parse(savedFilters);
      setFilters((prev) => ({ ...prev, ...parsedFilters }));
    }
  }, []);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    const filtersToSave = { ...filters };
    // Remove empty values before saving
    Object.keys(filtersToSave).forEach((key) => {
      if (
        filtersToSave[key] === "" ||
        filtersToSave[key] === null ||
        filtersToSave[key] === undefined
      ) {
        delete filtersToSave[key];
      }
    });
    localStorage.setItem("closeTicketFilters", JSON.stringify(filtersToSave));
  }, [filters]);

  useEffect(() => {
    const getAllClients = () => {
      setClientsLoading(true);
      new AdminApiModule()
        .getAllClients()
        .then((res) => {
          let parsedData = [];
          if (res && res.data && res.data.length > 0) {
            parsedData = res.data;
          }

          const clientOptions = parsedData.map((client) => ({
            value: client.client_id,
            label: client.client_name || client.client_id,
          }));
          setClients(clientOptions);
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {
          setClientsLoading(false);
        });
    };

    getAllClients();
  }, []);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDateChange = (type, date) => {
    if (type === "start") {
      handleFilterChange(
        "start_date",
        date ? moment(date).format("YYYY-MM-DD") : ""
      );
    } else {
      handleFilterChange(
        "end_date",
        date ? moment(date).format("YYYY-MM-DD") : ""
      );
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Process boolean values
    const processedFilters = { ...filters };

    Object.keys(processedFilters).forEach((key) => {
      if (processedFilters[key] === "true") {
        processedFilters[key] = true;
      } else if (processedFilters[key] === "false") {
        processedFilters[key] = false;
      }
    });

    // Remove empty filters
    const activeFilters = Object.fromEntries(
      Object.entries(processedFilters).filter(
        ([key, value]) => value !== "" && value !== null && value !== undefined
      )
    );

    onFilterSubmit(activeFilters);
  };

  const handleClearFilter = (field = null) => {
    if (field) {
      setFilters((prev) => {
        const newFilters = { ...prev };
        delete newFilters[field];

        // Update localStorage
        const filtersToSave = { ...newFilters };
        Object.keys(filtersToSave).forEach((key) => {
          if (
            filtersToSave[key] === "" ||
            filtersToSave[key] === null ||
            filtersToSave[key] === undefined
          ) {
            delete filtersToSave[key];
          }
        });
        localStorage.setItem(
          "closeTicketFilters",
          JSON.stringify(filtersToSave)
        );

        return newFilters;
      });
    } else {
      const defaultFilters = {
        start_date: moment().subtract(1, "month").format("YYYY-MM-DD"),
        end_date: moment().format("YYYY-MM-DD"),
      };
      setFilters(defaultFilters);
      localStorage.setItem(
        "closeTicketFilters",
        JSON.stringify(defaultFilters)
      );
      if (onClearFilters) {
        onClearFilters();
      }
    }
  };

  const getActiveFiltersCount = () => {
    return Object.keys(filters).filter(
      (key) =>
        filters[key] !== "" &&
        filters[key] !== null &&
        filters[key] !== undefined &&
        !(
          key === "start_date" &&
          filters[key] === moment().subtract(1, "month").format("YYYY-MM-DD") &&
          key === "end_date" &&
          filters[key] === moment().format("YYYY-MM-DD")
        )
    ).length;
  };

  const hasActiveFilters = getActiveFiltersCount() > 0;

  const formatDateForDisplay = (dateString) => {
    return moment(dateString).format("DD/MM/YYYY");
  };

  const getBooleanFilterValue = (field) => {
    const value = filters[field];
    if (value === true) return "true";
    if (value === false) return "false";
    if (value === "true") return "true";
    if (value === "false") return "false";
    return "";
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Paper className="mb-6 shadow-lg border border-gray-200 rounded-xl">
        {/* Filter Header */}
        <Box
          className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-t-xl border-b border-gray-200 cursor-pointer"
          onClick={() => setExpanded(!expanded)}
        >
          <Box className="flex items-center justify-between">
            <Box className="flex items-center space-x-3">
              <FilterListIcon className="text-purple-600" />
              <Typography variant="h6" className="font-semibold text-gray-800">
                Closed Requests Filters
              </Typography>
              {hasActiveFilters && (
                <Chip
                  label={`${getActiveFiltersCount()} active`}
                  size="small"
                  color="primary"
                  variant="filled"
                />
              )}
            </Box>
            <IconButton size="small">
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        {/* Active Filters Chips */}
        {hasActiveFilters && (
          <Box className="p-4 bg-gray-50 border-b border-gray-200">
            <Box className="flex items-center space-x-2 mb-2">
              <Typography variant="body2" className="font-medium text-gray-700">
                Active Filters:
              </Typography>
              <Button
                size="small"
                onClick={() => handleClearFilter()}
                className="text-red-600 hover:text-red-800"
                startIcon={<ClearIcon />}
              >
                Clear All
              </Button>
            </Box>
            <Box className="flex flex-wrap gap-2">
              {Object.entries(filters).map(([key, value]) => {
                if (!value || value === "") return null;

                let displayValue = value;
                let displayKey = key
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (l) => l.toUpperCase());

                if (typeof value === "boolean") {
                  displayValue = value ? "Yes" : "No";
                }

                if (key === "start_date" || key === "end_date") {
                  displayValue = formatDateForDisplay(value);
                }

                if (key === "ticket_created_by") {
                  const option = ticketCreatedByOptions.find(
                    (opt) => opt.value === value
                  );
                  displayValue = option ? option.label : value;
                } else if (key === "client_id") {
                  const option = clients.find((opt) => opt.value === value);
                  displayValue = option ? option.label : value;
                } else if (
                  key === "is_paid_by_public" ||
                  key === "is_paid_by_technician"
                ) {
                  if (value === true || value === "true") displayValue = "Yes";
                  else if (value === false || value === "false")
                    displayValue = "No";
                }

                return (
                  <Chip
                    key={key}
                    label={`${displayKey}: ${displayValue}`}
                    onDelete={() => handleClearFilter(key)}
                    color="primary"
                    variant="outlined"
                    size="medium"
                  />
                );
              })}
            </Box>
          </Box>
        )}

        {/* Filter Form */}
        <Collapse in={expanded}>
          <Box className="p-6">
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* Date Range Filters */}
                <Grid item xs={12} sm={6} md={3}>
                  <DatePicker
                    label="Start Date"
                    value={
                      filters.start_date ? moment(filters.start_date) : null
                    }
                    onChange={(date) => handleDateChange("start", date)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "medium",
                        variant: "outlined",
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <DatePicker
                    label="End Date"
                    value={filters.end_date ? moment(filters.end_date) : null}
                    onChange={(date) => handleDateChange("end", date)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "medium",
                        variant: "outlined",
                      },
                    }}
                  />
                </Grid>

                {/* Closed Date Filter */}
                <Grid item xs={12} sm={6} md={3}>
                  <DatePicker
                    label="Closed Date"
                    value={filters.closedAt ? moment(filters.closedAt) : null}
                    onChange={(date) =>
                      handleFilterChange(
                        "closedAt",
                        date ? moment(date).format("YYYY-MM-DD") : ""
                      )
                    }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "medium",
                        variant: "outlined",
                      },
                    }}
                  />
                </Grid>

                {/* Text-based Filters */}
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Request ID"
                    value={filters.ticket_id || ""}
                    onChange={(e) =>
                      handleFilterChange("ticket_id", e.target.value)
                    }
                    variant="outlined"
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Serial Number"
                    value={filters.serial_number || ""}
                    onChange={(e) =>
                      handleFilterChange("serial_number", e.target.value)
                    }
                    variant="outlined"
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Pincode"
                    value={filters.pincode || ""}
                    onChange={(e) =>
                      handleFilterChange("pincode", e.target.value)
                    }
                    variant="outlined"
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="City"
                    value={filters.city || ""}
                    onChange={(e) => handleFilterChange("city", e.target.value)}
                    variant="outlined"
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Customer Name"
                    value={filters.name || ""}
                    onChange={(e) => handleFilterChange("name", e.target.value)}
                    variant="outlined"
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={filters.phone_number || ""}
                    onChange={(e) =>
                      handleFilterChange("phone_number", e.target.value)
                    }
                    variant="outlined"
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="State"
                    value={filters.state || ""}
                    onChange={(e) =>
                      handleFilterChange("state", e.target.value)
                    }
                    variant="outlined"
                    size="small"
                  />
                </Grid>

                {/* Client ID Filters */}
                <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 220 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Client</InputLabel>
                    <Select
                      value={filters.client_id || ""}
                      label="Client"
                      onChange={(e) =>
                        handleFilterChange("client_id", e.target.value)
                      }
                      disabled={clientsLoading}
                    >
                      <MenuItem value="">All</MenuItem>
                      {clients.map((client) => (
                        <MenuItem key={client.value} value={client.value}>
                          {client.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Dropdown Filters */}
                <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 220 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Request Created By</InputLabel>
                    <Select
                      value={filters.request_created_by || ""}
                      label="Request Created By"
                      onChange={(e) =>
                        handleFilterChange("request_created_by", e.target.value)
                      }
                    >
                      <MenuItem value="">All</MenuItem>
                      {ticketCreatedByOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Boolean Filters */}
                <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 220 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Paid by Public</InputLabel>
                    <Select
                      value={getBooleanFilterValue("is_paid_by_public")}
                      label="Paid by Public"
                      onChange={(e) =>
                        handleFilterChange("is_paid_by_public", e.target.value)
                      }
                    >
                      {booleanOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 220 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Paid by Technician</InputLabel>
                    <Select
                      value={getBooleanFilterValue("is_paid_by_technician")}
                      label="Paid by Technician"
                      onChange={(e) =>
                        handleFilterChange(
                          "is_paid_by_technician",
                          e.target.value
                        )
                      }
                    >
                      {booleanOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Action Buttons */}
                <Grid item xs={12}>
                  <Box className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                    <Button
                      variant="outlined"
                      onClick={() => handleClearFilter()}
                      disabled={!hasActiveFilters}
                      className="min-w-24"
                    >
                      Clear All
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={loading}
                      className="min-w-24 bg-purple-600 hover:bg-purple-700"
                    >
                      {loading ? "Applying..." : "Apply Filters"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Box>
        </Collapse>
      </Paper>
    </LocalizationProvider>
  );
};

export default CloseTicketFilter;
